import {Component, Input, OnInit} from '@angular/core';
import {AggregatedDataPoint, BuildingType, DataType, Location} from "@flowmaps/flowmaps-typescriptmodels";
import {EnergyCompassUtils} from "../../charts/energy-compass-chart/energy-compass.utils";
import lodash from "lodash";
import moment from "moment";
import {RefdataUtils} from "../../refdata/refdata-utils";
import {interpolate} from "../../../common/utils";
import {Handler} from "../../../common/handler";
import {View} from "../../../common/view";
import {ChartDataPerMeasurement} from "../../../utils/chart-data-provider";

@Component({
    selector: 'app-location-energy-compass',
    templateUrl: './location-energy-compass.component.html',
    styleUrls: ['./location-energy-compass.component.scss']
})
@Handler()
export class LocationEnergyCompassComponent extends View implements OnInit {
    refDataUtils = RefdataUtils;
    _location: Location;
    buildingType: BuildingType;

    values: Record[] = [];

    measurements: DataType[] = [DataType.electricityConsumption, DataType.electricityConsumptionOffPeak];

    @Input()
    set location(location: Location) {
        this._location = location;
        if (location && location.info.buildingType) {
            this.sendQuery("getBuildingType", location.info.buildingType).subscribe(b => this.buildingType = b);
        }
    }

    ngOnInit() {
        const currentYear = moment().year();
        this.subscribeTo("getMeasurementsData")
            .subscribe((d: ChartDataPerMeasurement) => {
                const area = RefdataUtils.getLocationArea(this._location);
                const values = d.totalsPerYear ? Object.entries(d.totalsPerYear.measurements)
                    .flatMap(e =>
                        e[1].map(dataPoint => (<AggregatedMeasurementDataPoint>{
                            timeRange: dataPoint.timeRange,
                            value: dataPoint.value,
                            measurementType: e[0] as DataType
                        }))
                    ) : [];
                this.values = Object.entries(lodash.groupBy(values, v => v.timeRange.start))
                    .map(e => ({
                        year: moment(e[0]).year(),
                        value: lodash.round(lodash.sum(e[1].map(e =>
                            EnergyCompassUtils.getValueWithFactor(e.measurementType, [e.value]))) / area, 1)
                    }))
                    .filter(r => r.year < currentYear)
                    .sort(RefdataUtils.locationEnergyCompassYearsComparator.compare);
            });
    }

    get parisProofValue(): number {
        return this.buildingType?.info.efficiencyScale["ParisProof"] || 0;
    }

    getBuildingBackgroundStyle(value: number): number {
        return Math.max(0, Math.min(80, 100 - interpolate(value, this.minValue, this.maxValue, 0, 100)));
    }

    get maxValue(): number {
        return lodash.max(this.values.map(v => v.value).concat(this.parisProofValue));
    }

    get minValue(): number {
        return lodash.min(this.values.map(v => v.value).concat(this.parisProofValue));
    }

    get noDataText(): string {
        if (!this._location) {
            return "No location selected";
        }
        if (!RefdataUtils.getLocationArea(this._location)) {
            return "No area defined, consumption per m² is unavailable";
        }
        if (this.values.length === 0) {
            return "No electricity consumption data available for this location";
        }
        return "";
    }
}

interface AggregatedMeasurementDataPoint extends AggregatedDataPoint {
    measurementType: DataType;
}

interface Record {
    year: number;
    value: number;
}
